<script setup>
import axios from 'axios';
import { ref, onMounted } from 'vue';
import Paginator from '@/components/datatables/Paginator.vue';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import "@vueform/multiselect/themes/default.css";
import { usePermissions } from '@/composables/usePermissions';

const rangeDateconfig = {
    mode: "range",
    dateFormat: "Y-m-d",
}

// state
const result = ref([]);
const loading = ref(false);
const filters = ref({
    key_search: '',
    payment_status: '',
    date: '',
});
const errorMessage = ref('');
const permissions = usePermissions();

// methods
const fetchData = async (page = 1) => {
    loading.value = true;
    try {
        const response = await axios.get(`${process.env.VUE_APP_AR_API_BASEURL}invoice/all`,{ 
            params: { page: page, ...filters.value } 
        });
        if (response.status != 200) {
            alert(response.data.message);
        }
        result.value = response.data;
    } catch (error) {
        console.error(error);
        errorMessage.value = 'Failed to load the data! Please try again later.';
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    filters.value = {
        key_search: '',
        payment_status: '',
        date: '',
    };    
    errorMessage.value = '';
    fetchData();
};

const onSearchHandler = (e) => {
    e.preventDefault();    
    fetchData();
};

const calculateTotal =(item)=> {
    const monthlyAmount = Number(item.total_amount);
    const latePaymentCharges = Number(item.late_payment_charges);
    const totalAmount = monthlyAmount + latePaymentCharges;
    return totalAmount.toFixed(2);
}

const calculateBalance = (item) => {
    const totalAmount = Number(item.total_amount);
    const latePaymentCharges = Number(item.late_payment_charges);
    const paidAmount = Number(item.paid_amount);
    const creditsApplied = Number(item.credits_applied);
    const over_pay = Number(item.over_pay);
    const balanceAmount = totalAmount - over_pay + latePaymentCharges - paidAmount - creditsApplied;
    return balanceAmount.toFixed(2);
}

// todo
// const downloadExcel = () =>{}

// lifecycle
onMounted(() => {
    axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
    fetchData();
});


</script>

<template>
    <div>
        <!-- searching -->
        <div>
            <div class="card-body border-bottom-dashed border-bottom">
                <form @submit="onSearchHandler">
                    <div class="row g-3">
                        <div class="col-xl-6">
                            <div class="search-box">
                                <input type="text" class="form-control search"
                                placeholder="Search for Ref. No., Customer Name" v-model="filters.key_search"/>
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-xl-6 pb-3">
                            <div class="row g-2">
                                <div class="col-sm-12">
                                    <div class="search-box"><i class="ri-search-line search-icon"></i>
                                        <flat-pickr v-model="filters.date" placeholder="Select Issue Date" :config="rangeDateconfig"
                                        class="form-control"></flat-pickr>
                                    </div>
                                </div>                   
                            </div>
                        </div>
                    </div>
                    <div class="row g-3">
                        <div class="col-xl-6">
                            <div class="search-box">
                                <select class="form-select mb-3" v-model='filters.payment_status'>
                                    <option value="" selected disabled>Search Status</option>
                                    <option value="Paid" selected >Paid</option>
                                    <option value="Partially Paid" selected >Partial Paid</option>
                                    <option value="Overdue" selected >Overdue</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="row g-3 justify-content-end">
                        <div class="col-xl-2">
                            <div>
                                <button type="submit" class="btn btn-primary w-100">
                                    <i class="ri-equalizer-fill me-2 align-bottom"></i>Search
                                </button>
                            </div>
                        </div>
                        <!--end col-->
                        <div class="col-xl-2">
                            <div>
                                <button type="button" @click="resetForm" class="btn btn-danger w-100">
                                    <i class="ri-refresh-fill me-2 align-bottom"></i>Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            <div class="card-body border-bottom-dashed border-bottom pb-3">
                <div class="row g-3 justify-content-end">
                    <div  class="col-xl-2">
                        <div v-if="permissions.canCreate?.value"> 
                            <router-link  :to="'/ar/invoices/create'" class="btn btn-success w-100"><i class="ri-add-fill me-2 align-bottom"></i> Create Invoice</router-link >
                        </div>
                    </div>
                    <!-- <div class="col-xl-2">
                        <div>
                            <button @click="downloadExcel()" class="btn btn-success w-100"><i class="ri-file-excel-2-line align-middle me-1"></i> Export Excel</button>
                        </div>
                    </div> -->
                </div>
            </div>
        </div>
        <div class="table-responsive mt-3">
            <table class="table table-striped table-nowrap mb-0">
                <thead class="table-secondary">
                    <tr>
                        <th>#.</th>
                        <th scope="col">Ref. No</th>           
                        <th scope="col">Customer Name</th>           
                        <th scope="col">Issue Date</th>
                        <th scope="col">Due Date</th>
                        <th scope="col">Monthly Amount (RM)</th>
                        <th scope="col">Overpaid Amount (RM)</th>
                        <th scope="col">Late Payment Charge (RM)</th>
                        <th scope="col">Paid Amount (RM)</th>           
                        <th scope="col">Credits Applied (RM)</th>
                        <th scope="col">Balance Amount (RM)</th>                                                      
                        <th scope="col">Status</th>
                        <th scope="col">Action</th>
                    </tr>                 
                </thead>
                <tbody>
                    <tr v-for="(item, index) in result.data" :key="item.id">
                        <td>{{ index + result.from }}</td>
                        <td>{{ item.invoice_no }}</td>
                        <td><router-link :to="'/customer/customer-details/'+item.customer_id" class="fw-medium link-secondary">{{item.name_as_per_nric}}</router-link></td>
                        <td>{{ item.issue_date }}</td>
                        <td>{{ item.due_date }}</td>
                        <td style="text-align: right;">{{ calculateTotal(item) }}</td>
                        <td style="text-align: right;">{{ parseFloat(item.over_pay).toFixed(2) }}</td>
                        <td style="text-align: right;">{{ item.late_payment_charges }}</td>
                        <td style="text-align: right;">{{ item.paid_amount }}</td>
                        <td style="text-align: right;">{{ item.credits_applied }}</td>
                        <td style="text-align: right;">{{ calculateBalance(item) }}</td>

                        <td v-if="item.status == 'Pending'"><span class="badge bg-primary">Pending</span></td>
                        <td v-else-if="item.status == 'Paid'"><span class="badge" style="background-color: #0AB39C">Paid</span></td>
                        <td v-else-if="item.status == 'Partial Payment'"><span class="badge" style="background-color: #F3C10F">Partial Paid</span></td>
                        <td v-else><span class="badge" style="background-color: #F06548">Overdue</span></td>                                   

                        <td>
                            <div class="dropdown">
                                <button class="btn btn-soft-secondary btn-sm dropdown" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    <i class="ri-more-fill align-middle"></i>
                                </button>
                                <ul class="dropdown-menu dropdown-menu-end">
                                    <li>
                                        <router-link class="dropdown-item" :to="'/ar/invoice/view/'+item.id"><i class="ri-eye-fill align-bottom me-2 text-muted"></i> View Monthly Statement Details</router-link>
                                    </li>
                                    <li v-if="permissions.canUpdate?.value">
                                        <router-link class="dropdown-item" :to="'/ar/credit_notes/create/'+item.customer_id"><i class="ri-file-text-line align-bottom me-2 text-muted"></i> Create Credit Note</router-link>
                                    </li>
                                    <li v-if="permissions.canCreate?.value">
                                        <router-link class="dropdown-item" :to="'/ar/instantpay/'+item.orderId"><i class="ri-secure-payment-fill align-bottom me-2 text-muted"></i> Generate Instant Pay</router-link>
                                    </li>
                                </ul>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="mt-4" style="display: flex; justify-content: space-between; align-items: center;">
                <div>Showing {{ result.from }} to {{ result.to }} of {{ result.total }} results</div>
                <Paginator :data="result" :limit="1" @pagination-change-page="fetchData"></Paginator>
            </div>
        </div>
    </div>
</template>